import { useState, useEffect, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN PSYCHOLOGY BAKED IN:
// 1. SCARCITY + URGENCY  — "Only 2 left", countdown timers, "Selling Fast"
// 2. SOCIAL PROOF        — Live viewer counts, recent purchases, review stars
// 3. IDENTITY ASPIRATION — "Women who choose Posh…" language throughout
// 4. AUTHORITY SIGNALS   — Curator language, "Expertly Selected", trust badges
// 5. RECIPROCITY         — 10% gift framing, free luxury packaging
// 6. LOSS AVERSION       — "Don't miss this", cart recovery, cart not reserved
// 7. COLOR PSYCHOLOGY    — Deep champagne/blush/noir (femininity + luxury)
// 8. EXCLUSIVITY         — Password gate, "Members only", private framing
// 9. SENSORY LANGUAGE    — Rich tactile descriptions in every product copy
// 10. COMMUNITY IDENTITY — "The Posh Woman" tribe identity language
// ─────────────────────────────────────────────────────────────────────────────

const SITE_PASSWORD = "luxevip";

// ── Shopify Storefront Config ─────────────────────────────────────────────────
const SHOPIFY_STORE = "poshboutiquenyc.myshopify.com";
const SHOPIFY_TOKEN = "e3c281e5bbc75f058ece6c3bea36e6f3";
const SHOPIFY_API = `https://${SHOPIFY_STORE}/api/2026-01/graphql.json`;

async function createShopifyCheckout(cartItems) {
  const lineItems = cartItems.map(item => ({
    variantId: item.shopifyVariantId || null,
    quantity: item.qty,
    title: item.name,
    price: item.price,
  }));

  // Build checkout URL — direct to Shopify store
  const checkoutUrl = `https://${SHOPIFY_STORE}/cart`;
  return checkoutUrl;
}

async function fetchShopifyProducts() {
  try {
    const res = await fetch(SHOPIFY_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_TOKEN,
      },
      body: JSON.stringify({
        query: `{
          products(first: 20) {
            edges {
              node {
                id
                title
                handle
                priceRange { minVariantPrice { amount } }
                images(first: 1) { edges { node { url } } }
                variants(first: 1) { edges { node { id } } }
              }
            }
          }
        }`
      })
    });
    const data = await res.json();
    return data?.data?.products?.edges || [];
  } catch(e) {
    console.log("Shopify fetch error:", e);
    return [];
  }
}

const C = {
  // Polène-inspired light palette
  bg:        "#faf9f7",       // warm off-white page background
  bgCard:    "#ffffff",       // pure white cards
  bgAlt:     "#f4f2ef",       // slightly warmer section bg
  header:    "rgba(250,249,247,0.97)", // frosted header
  noir:      "#1a1816",       // near-black text
  dark:      "#2c2a28",       // secondary dark
  mid:       "#6b6560",       // medium gray-brown
  muted:     "#a09890",       // muted labels
  light:     "#d4cfc9",       // borders, dividers
  accent:    "#8b7355",       // warm taupe accent (Polène-like)
  accentLight:"#c9bfb0",      // light taupe
  rose:      "#c4849a",       // kept for psychological urgency
  urgent:    "#b84060",       // darker, more refined urgent
  urgentBg:  "rgba(184,64,96,0.07)",
  gold:      "#a08050",       // muted gold
  // Aliases to keep existing code working
  champagne: "#1a1816",
  deepWine:  "#f4f2ef",
  richPlum:  "#ede9e3",
  dustyRose: "#8b7355",
  roseGold:  "#a09078",
  subtext:   "#6b6560",
  mauve:     "#a09890",
  text:      "#1a1816",
};

const PRODUCTS = {
  // ── NEW ──────────────────────────────────────────────────────────────────
  new: {
    bags: [
      { id:101, name:"Hobo Crescent", brand:"Bottega Style", price:298, originalPrice:null, color:"Intrecciato Sand", img:"👜", tag:"New" },
      { id:102, name:"Bamboo Handle Bag", brand:"Gucci Inspired", price:355, originalPrice:null, color:"Ivory Canvas", img:"👝", tag:"New" },
      { id:103, name:"Wicker Basket Bag", brand:"Dior Style", price:245, originalPrice:null, color:"Natural Wicker", img:"💼", tag:"New" },
      { id:104, name:"Lady Dior Mini", brand:"Dior Style", price:389, originalPrice:null, color:"Blush Pink Cannage", img:"👛", tag:"New" },
    ],
    accessories: [
      { id:105, name:"Silk Logo Scarf", brand:"Hermès Style", price:129, originalPrice:null, color:"Rose & Gold", img:"🧣", tag:"New" },
      { id:106, name:"Chain Belt", brand:"Chanel Inspired", price:98, originalPrice:null, color:"Gold Hardware", img:"📿", tag:"New" },
      { id:107, name:"Logo Card Holder", brand:"LV Style", price:149, originalPrice:null, color:"Monogram Canvas", img:"💳", tag:"New" },
      { id:108, name:"Sunglasses Case", brand:"Gucci Inspired", price:89, originalPrice:null, color:"GG Black", img:"🕶️", tag:"New" },
    ],
  },
  // ── BAGS ─────────────────────────────────────────────────────────────────
  bags: {
    bestSellers: [
      { id:1, name:"Quilted Chain Flap", brand:"Chanel Inspired", price:289, originalPrice:350, color:"Classic Black", img:"👜", tag:"Best Seller" },
      { id:2, name:"Neverfull Tote", brand:"LV Style", price:345, originalPrice:420, color:"Monogram Tan", img:"👝", tag:"Best Seller" },
      { id:3, name:"Nano Speedy", brand:"LV Style", price:198, originalPrice:240, color:"Damier Ebène", img:"💼", tag:"Best Seller" },
      { id:4, name:"GG Marmont Mini", brand:"Gucci Inspired", price:265, originalPrice:310, color:"Matelassé White", img:"👛", tag:"Best Seller" },
    ],
    backInStock: [
      { id:5, name:"Saddle Bag", brand:"Dior Style", price:320, originalPrice:380, color:"Cognac Leather", img:"👜", tag:"Back In Stock" },
      { id:6, name:"Trapeze Mini", brand:"Céline Inspired", price:275, originalPrice:330, color:"Camel Suede", img:"👝", tag:"Back In Stock" },
      { id:7, name:"Belt Bag", brand:"Fendi Style", price:189, originalPrice:230, color:"Zucca Canvas", img:"💼", tag:"Back In Stock" },
      { id:8, name:"City Bag Classic", brand:"Balenciaga Style", price:310, originalPrice:375, color:"Storm Grey", img:"👛", tag:"Back In Stock" },
    ],
    lightningDeals: [
      { id:13, name:"Structured Satchel", brand:"Hermès Style", price:149, originalPrice:320, color:"Gold Hardware", img:"👜", tag:"Lightning Deal", timeLeft:7200 },
      { id:14, name:"Kelly Mini", brand:"Hermès Style", price:199, originalPrice:450, color:"Rouge Tomate", img:"👝", tag:"Lightning Deal", timeLeft:3600 },
      { id:15, name:"Birkin 25 Style", brand:"Hermès Style", price:259, originalPrice:580, color:"Etoupe Swift", img:"💼", tag:"Lightning Deal", timeLeft:14400 },
      { id:16, name:"Constance Slim", brand:"Hermès Style", price:179, originalPrice:390, color:"Black Epsom", img:"👛", tag:"Lightning Deal", timeLeft:1800 },
    ],
  },
  // ── ACCESSORIES ──────────────────────────────────────────────────────────
  accessories: {
    bestSellers: [
      { id:201, name:"Silk Logo Scarf", brand:"Hermès Style", price:129, originalPrice:180, color:"Ivory & Gold", img:"🧣", tag:"Best Seller" },
      { id:202, name:"Chain Belt", brand:"Chanel Inspired", price:98, originalPrice:140, color:"Gold Hardware", img:"📿", tag:"Best Seller" },
      { id:203, name:"Logo Card Holder", brand:"LV Style", price:149, originalPrice:200, color:"Monogram Canvas", img:"💳", tag:"Best Seller" },
      { id:204, name:"Logo Sunglasses", brand:"Gucci Inspired", price:175, originalPrice:240, color:"Tortoise Shell", img:"🕶️", tag:"Best Seller" },
    ],
    backInStock: [
      { id:205, name:"Coin Purse", brand:"Dior Style", price:89, originalPrice:120, color:"Blush Pink", img:"👛", tag:"Back In Stock" },
      { id:206, name:"Luggage Tag", brand:"LV Style", price:69, originalPrice:95, color:"Monogram Tan", img:"🏷️", tag:"Back In Stock" },
      { id:207, name:"Key Holder", brand:"Chanel Inspired", price:79, originalPrice:110, color:"Black Caviar", img:"🔑", tag:"Back In Stock" },
      { id:208, name:"Wristlet Strap", brand:"Bottega Style", price:115, originalPrice:160, color:"Intrecciato Tan", img:"🤝", tag:"Back In Stock" },
    ],
    lightningDeals: [
      { id:209, name:"Logo Hair Scarf", brand:"Gucci Inspired", price:45, originalPrice:110, color:"Red & Green Stripe", img:"🎀", tag:"Lightning Deal", timeLeft:5400 },
      { id:210, name:"Mini Wallet", brand:"Dior Style", price:69, originalPrice:165, color:"Cannage Rose", img:"💼", tag:"Lightning Deal", timeLeft:9000 },
      { id:211, name:"Bag Charm", brand:"LV Style", price:55, originalPrice:130, color:"Gold Monogram", img:"✨", tag:"Lightning Deal", timeLeft:3600 },
      { id:212, name:"Silk Twilly", brand:"Hermès Style", price:59, originalPrice:145, color:"Orange & Pink", img:"🎗️", tag:"Lightning Deal", timeLeft:7200 },
    ],
  },
};

const LIVE_SALES = [
  { date:"Saturday, March 15, 2026", time:"7:00 PM EST", platform:"Facebook Live", theme:"Spring Collection Drop", watching:312 },
  { date:"Wednesday, March 19, 2026", time:"8:00 PM EST", platform:"Facebook Live", theme:"Lightning Deal Blitz", watching:0 },
  { date:"Saturday, March 22, 2026", time:"6:00 PM EST", platform:"Facebook Live", theme:"New Arrivals Showcase", watching:0 },
];

function formatTime(s) {
  const h=Math.floor(s/3600), m=Math.floor((s%3600)/60), sec=s%60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
}


// ─── PASSWORD GATE ────────────────────────────────────────────────────────────
function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState(""); const [error, setError] = useState(false); const [shake, setShake] = useState(false);
  const attempt = () => { if (pw===SITE_PASSWORD) { onUnlock(); } else { setError(true); setShake(true); setTimeout(()=>setShake(false),600); } };
  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:0, padding:24, position:"relative", overflow:"hidden" }}>
      
      <div style={{ textAlign:"center", marginBottom:44, position:"relative" }}>
        <div style={{ fontSize:12, letterSpacing:8, color:C.mauve, fontFamily:"sans-serif", marginBottom:18, fontWeight:300 }}>WELCOME TO</div>
        <div style={{ fontFamily:"'Cormorant Garamond', Georgia, serif", fontSize:60, fontWeight:400, letterSpacing:10, background:`linear-gradient(135deg, ${C.dustyRose}, ${C.champagne}, ${C.gold})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", lineHeight:1 }}>POSH</div>
        <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, letterSpacing:14, color:C.mauve, fontWeight:300, marginTop:4 }}>BOUTIQUE</div>
        <div style={{ width:56, height:1, background:`linear-gradient(to right, transparent, ${C.roseGold}, transparent)`, margin:"18px auto 0" }}/>
        <div style={{ fontSize:12, letterSpacing:2, color:C.muted, fontFamily:"sans-serif", marginTop:14, fontStyle:"italic" }}>Curated Designer Bags for the Woman Who Knows</div>
      </div>
      <div style={{ background:"rgba(255,255,255,0.03)", border:`1px solid rgba(196,132,154,0.25)`, borderRadius:20, padding:"36px 44px", display:"flex", flexDirection:"column", gap:18, backdropFilter:"blur(24px)", minWidth:340, maxWidth:380, animation:shake?"shake 0.5s":"none", boxShadow:"0 40px 80px rgba(0,0,0,0.5)" }}>
        <div style={{ color:C.subtext, fontSize:11, letterSpacing:4, textAlign:"center", fontFamily:"sans-serif" }}>🔒 PRIVATE BOUTIQUE ACCESS</div>
        <input type="password" placeholder="Enter your password" value={pw} onChange={e=>{setPw(e.target.value);setError(false);}} onKeyDown={e=>e.key==="Enter"&&attempt()} style={{ background:"rgba(255,255,255,0.05)", border:`1px solid ${error?C.urgent:"rgba(196,132,154,0.25)"}`, color:C.champagne, padding:"14px 18px", borderRadius:10, fontSize:14, outline:"none", fontFamily:"sans-serif", letterSpacing:4, textAlign:"center", transition:"border 0.3s" }}/>
        {error && <div style={{ color:C.urgent, fontSize:11, textAlign:"center", fontFamily:"sans-serif" }}>Incorrect password — please try again</div>}
        <button onClick={attempt} style={{ background:`linear-gradient(135deg, ${C.dustyRose}, ${C.roseGold})`, color:"#fff", border:"none", padding:"15px", borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer", letterSpacing:4, fontFamily:"sans-serif", boxShadow:`0 8px 24px rgba(196,132,154,0.35)`, transition:"all 0.3s" }} onMouseEnter={e=>e.target.style.transform="translateY(-2px)"} onMouseLeave={e=>e.target.style.transform="none"}>ENTER THE BOUTIQUE</button>
        <div style={{ fontSize:11, color:C.muted, textAlign:"center", fontFamily:"sans-serif", fontStyle:"italic" }}>Members only · Exclusively yours</div>
      </div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Raleway:wght@300;400;600;700&display=swap');
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 60%{transform:translateX(8px)} 80%{transform:translateX(-4px)} }
      `}</style>
    </div>
  );
}

// ─── SIGNUP POPUP ─────────────────────────────────────────────────────────────
function SignupPopup({ onClose }) {
  const [email, setEmail] = useState(""); const [phone, setPhone] = useState(""); const [done, setDone] = useState(false);
  const submit = () => {
    if (email||phone) {
      localStorage.setItem("posh_signed_up", "true");
      setDone(true);
    }
  };
  const inputStyle = { width:"100%", background:"#fff", border:`1px solid ${C.light}`, color:C.noir, padding:"13px 16px", borderRadius:10, fontSize:14, outline:"none", fontFamily:"sans-serif", boxSizing:"border-box" };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"#fff", border:`1px solid ${C.light}`, borderRadius:4, padding:"32px 24px", maxWidth:420, width:"90%", position:"relative", textAlign:"center", boxShadow:"0 40px 100px rgba(0,0,0,0.9)" }}>
        <button onClick={onClose} style={{ position:"absolute", top:18, right:22, background:"none", border:"none", color:C.mid, fontSize:22, cursor:"pointer" }}>×</button>
        {!done ? (
          <>
            <div style={{ fontSize:28, marginBottom:16 }}>💖</div>
            <div style={{ color:C.noir, fontFamily:"'Cormorant Garamond', serif", fontSize:28, fontWeight:300, marginBottom:8, lineHeight:1.2 }}>You deserve first access.</div>
            <div style={{ color:C.mid, fontSize:12, marginBottom:12, fontFamily:"'Raleway',sans-serif", lineHeight:1.8, fontWeight:300 }}>Join the women who never miss a drop.</div>
            <div style={{ background:C.bgAlt, border:`1px solid ${C.light}`, borderRadius:2, padding:"20px 24px", marginBottom:22, textAlign:"center" }}>
              <div style={{ fontSize:11, letterSpacing:3, fontFamily:"sans-serif", color:C.subtext, marginBottom:8, textTransform:"uppercase" }}>Your Welcome Gift</div>
              <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:36, fontWeight:300, color:C.noir, lineHeight:1, fontFamily:"'Cormorant Garamond',serif" }}>10% Off</div>
              <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:15, fontStyle:"italic", color:C.gold, marginTop:6 }}>your first order</div>
              <div style={{ color:C.muted, fontSize:9, fontFamily:"'Raleway',sans-serif", letterSpacing:1, marginTop:10 }}>Cannot be combined with other offers</div>
            </div>
            <input type="email" placeholder="Your email address" value={email} onChange={e=>setEmail(e.target.value)} style={{...inputStyle, marginBottom:10}} />
            <input type="tel" placeholder="Phone number (exclusive text alerts)" value={phone} onChange={e=>setPhone(e.target.value)} style={{...inputStyle, marginBottom:18}} />
            <button onClick={submit} style={{ width:"100%", background:C.noir, color:"#fff", border:"none", padding:"15px", borderRadius:2, fontSize:10, fontWeight:600, cursor:"pointer", letterSpacing:3.5, fontFamily:"'Raleway',sans-serif" }}>CLAIM MY WELCOME GIFT →</button>
            <div style={{ color:C.muted, fontSize:10, marginTop:14, cursor:"pointer", fontFamily:"'Raleway',sans-serif", letterSpacing:0.5, textDecoration:"underline" }} onClick={onClose}>I prefer to pay full price</div>
          </>
        ) : (
          <>
            <div style={{ fontSize:44, marginBottom:16 }}>🎀</div>
            <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:28, marginBottom:12 }}>Welcome to the family, gorgeous.</div>
            <div style={{ color:C.subtext, fontSize:14, fontFamily:"sans-serif", lineHeight:1.9 }}>
              Your code <strong style={{ color:C.gold }}>POSH10</strong> has been sent.<br/>
              You're now part of an exclusive circle of women<br/>who carry nothing but the best. 💕
            </div>
            <button onClick={onClose} style={{ marginTop:28, background:`linear-gradient(135deg, ${C.dustyRose}, ${C.roseGold})`, color:"#fff", border:"none", padding:"13px 36px", borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer", letterSpacing:3, fontFamily:"sans-serif" }}>START SHOPPING</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CART RECOVERY ────────────────────────────────────────────────────────────
function CartRecoveryBanner({ cartItems, onRecover, onDismiss }) {
  if (!cartItems.length) return null;
  return (
    <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", background:`linear-gradient(135deg, ${C.deepWine}, #2d0e1f)`, border:`1px solid rgba(196,132,154,0.4)`, borderRadius:16, padding:"18px 28px", zIndex:900, boxShadow:"0 12px 50px rgba(0,0,0,0.7)", display:"flex", alignItems:"center", gap:20, maxWidth:520, width:"90%", animation:"slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)" }}>
      <div style={{ fontSize:30 }}>💔</div>
      <div style={{ flex:1 }}>
        <div style={{ color:C.champagne, fontSize:15, fontFamily:"'Cormorant Garamond', serif", fontWeight:600 }}>Your bag is waiting for you…</div>
        <div style={{ color:C.mid, fontSize:11, fontFamily:"'Raleway',sans-serif", marginTop:3 }}>{cartItems.length} item{cartItems.length>1?"s":""} in your cart · someone else might grab it first</div>
      </div>
      <button onClick={onRecover} style={{ background:`linear-gradient(135deg, ${C.dustyRose}, ${C.roseGold})`, color:"#fff", border:"none", padding:"10px 18px", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", letterSpacing:2, fontFamily:"sans-serif", whiteSpace:"nowrap" }}>COMPLETE MY ORDER</button>
      <button onClick={onDismiss} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:18 }}>×</button>
      <style>{`@keyframes slideUp { from{transform:translateX(-50%) translateY(80px);opacity:0} to{transform:translateX(-50%) translateY(0);opacity:1} }`}</style>
    </div>
  );
}

// ─── PRODUCT CARD ─────────────────────────────────────────────────────────────
function ProductCard({ product, onAddToCart }) {
  const [added, setAdded] = useState(false); const [hovered, setHovered] = useState(false);
  const discount = product.originalPrice ? Math.round(((product.originalPrice-product.price)/product.originalPrice)*100) : null;
  const handleAdd = () => { onAddToCart(product); setAdded(true); setTimeout(()=>setAdded(false),2500); };
  return (
    <div onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)} style={{ background:C.bgCard, border:`1px solid ${hovered?C.light:"transparent"}`, borderRadius:4, overflow:"hidden", cursor:"pointer", transform:hovered?"translateY(-4px)":"none", transition:"all 0.35s ease", boxShadow:hovered?"0 8px 32px rgba(0,0,0,0.08)":"none" }}>
      <div style={{ height:180, position:"relative", background:C.bgAlt, display:"flex", alignItems:"center", justifyContent:"center", fontSize:72, overflow:"hidden" }}>
        <div style={{ transition:"all 0.4s", transform:hovered?"scale(1.1)":"scale(1)", filter:hovered?"drop-shadow(0 0 24px rgba(212,168,83,0.5))":"none" }}>{product.img}</div>
        <div style={{ position:"absolute", top:12, left:12, background:product.tag==="Lightning Deal"?C.urgent:product.tag==="Back In Stock"?"#4a7a5a":C.accent, color:"#fff", fontSize:9, fontWeight:600, padding:"4px 10px", borderRadius:2, letterSpacing:1.5, fontFamily:"'Raleway',sans-serif" }}>
          {product.tag==="Best Seller"?"🔥 BESTSELLER":product.tag==="Back In Stock"?"✅ BACK IN STOCK":product.tag==="New Arrival"?"✨ NEW":"⚡ DEAL"}
        </div>
        {discount && <div style={{ position:"absolute", top:12, right:12, background:C.urgent, color:"#fff", fontSize:9, fontWeight:600, padding:"4px 8px", borderRadius:2, fontFamily:"'Raleway',sans-serif" }}>−{discount}%</div>}
        {product.timeLeft!==undefined && <div style={{ position:"absolute", bottom:8, left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.85)", color:C.gold, fontSize:12, padding:"5px 14px", borderRadius:20, fontFamily:"monospace", border:`1px solid rgba(212,168,83,0.3)`, fontWeight:700, letterSpacing:1 }}>⚡ {formatTime(product.timeLeft)}</div>}
      </div>
      <div style={{ padding:"18px 20px 20px" }}>
        <div style={{ color:C.muted, fontSize:9, letterSpacing:3, fontFamily:"'Raleway',sans-serif", marginBottom:6, textTransform:"uppercase" }}>{product.brand}</div>
        <div style={{ color:C.noir, fontFamily:"'Cormorant Garamond', serif", fontSize:16, fontWeight:400, marginBottom:4, lineHeight:1.3 }}>{product.name}</div>
        <div style={{ color:C.mid, fontSize:11, fontFamily:"'Raleway',sans-serif", marginBottom:10, fontStyle:"normal", letterSpacing:0.5 }}>{product.color}</div>

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
          <div>
            <span style={{ color:C.noir, fontSize:16, fontWeight:400, fontFamily:"'Cormorant Garamond', serif" }}>${product.price}</span>
            {product.originalPrice && <span style={{ color:C.muted, fontSize:13, textDecoration:"line-through", marginLeft:8, fontFamily:"'Raleway',sans-serif" }}>${product.originalPrice}</span>}
          </div>
          <button onClick={handleAdd} style={{ background:added?"rgba(80,200,120,0.15)":`linear-gradient(135deg,${C.dustyRose},${C.roseGold})`, color:added?"#50c878":"#fff", border:added?`1px solid #50c878`:"none", padding:"9px 16px", borderRadius:8, fontSize:10, fontWeight:700, cursor:"pointer", letterSpacing:1.5, fontFamily:"sans-serif", transition:"all 0.3s", whiteSpace:"nowrap" }}>
            {added?"✓ ADDED!":"ADD TO CART"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TRUST BAR ────────────────────────────────────────────────────────────────
function TrustBar() {
  return (
    <div className="trust-bar" style={{ background:`linear-gradient(135deg,rgba(196,132,154,0.06),rgba(212,168,83,0.03))`, borderTop:`1px solid rgba(196,132,154,0.1)`, borderBottom:`1px solid ${C.light}`, padding:"12px 0" }}>
      <div style={{ display:"flex", gap:36, justifyContent:"center", flexWrap:"wrap", padding:"0 32px" }}>
        {[["🔒","Secure Shopify Checkout"],["⚡","Ships in 7–9 Business Days"],["💎","Expertly Curated Pieces"]].map(([icon,text],i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:8, whiteSpace:"nowrap" }}>
            <span style={{ fontSize:14 }}>{icon}</span>
            <span style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:10, letterSpacing:1.5, textTransform:"uppercase" }}>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SHOP PAGE ────────────────────────────────────────────────────────────────
function ShopPage({ onAddToCart, megaNav, clearMegaNav }) {
  const [mainTab, setMainTab] = useState("new");
  const [subTab, setSubTab] = useState("bags");
  const [innerTab, setInnerTab] = useState("bestSellers");

  useEffect(() => {
    if (megaNav) {
      setMainTab(megaNav.main);
      if (megaNav.sub) setSubTab(megaNav.sub);
      if (megaNav.inner) setInnerTab(megaNav.inner);
      clearMegaNav();
    }
  }, [megaNav]);
  const getProducts = () => {
    if (mainTab === "new") {
      if (subTab === "all") return [...(PRODUCTS.new.bags || []), ...(PRODUCTS.new.accessories || [])];
      return PRODUCTS.new[subTab] || [];
    }
    if (mainTab === "bags") return PRODUCTS.bags[innerTab] || [];
    if (mainTab === "accessories") return PRODUCTS.accessories[innerTab] || [];
    return [];
  };

  const isLightning = (mainTab === "bags" && innerTab === "lightningDeals") ||
                      (mainTab === "accessories" && innerTab === "lightningDeals");

  return (
    <div>
      {/* Hero banner */}
      <div className="hero-wrap" style={{ textAlign:"center", marginBottom:40, padding:"clamp(32px,6vw,72px) clamp(16px,4vw,32px) clamp(24px,4vw,56px)" }}>
        
        <div className="hero-label" style={{ color:C.muted, fontSize:9, letterSpacing:5, fontFamily:"'Raleway',sans-serif", marginBottom:16, textTransform:"uppercase" }}>The Posh Collection</div>
        <h1 style={{ color:C.noir, fontFamily:"'Cormorant Garamond', serif", fontSize:"clamp(26px,4.5vw,56px)", fontWeight:300, margin:"0 0 16px", lineHeight:1.1 }}>
          Carry the bag. <em style={{ color:C.accent, fontStyle:"italic" }}>Own the room.</em>
        </h1>
        <p className="hero-sub" style={{ color:C.mid, fontFamily:"'Raleway',sans-serif", fontSize:13, lineHeight:2, maxWidth:480, margin:"0 auto", fontWeight:300, letterSpacing:0.3 }}>
          Every piece in our collection is hand-selected by our curators — because a Posh woman doesn't settle for anything less than extraordinary.
        </p>
      </div>





      {/* Product grid */}
      <div className="products-grid mob-hide" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%/2 - 8px, 250px), 1fr))", gap:"clamp(12px,2vw,24px)" }}>
        {getProducts().map(p => <ProductCard key={p.id} product={p} onAddToCart={onAddToCart}/>)}
      </div>
    </div>
  );
}

// ─── CART PAGE ────────────────────────────────────────────────────────────────
function CartPage({ cartItems, setCartItems }) {
  const [checkout, setCheckout] = useState(false); const [confirmed, setConfirmed] = useState(false);
  const total = cartItems.reduce((s,i)=>s+i.price*i.qty,0);
  const updateQty = (id,d) => setCartItems(prev=>prev.map(i=>i.id===id?{...i,qty:Math.max(0,i.qty+d)}:i).filter(i=>i.qty>0));
  const inputStyle = { width:"100%", background:"#fff", border:`1px solid ${C.light}`, color:C.noir, padding:"13px 16px", borderRadius:10, fontSize:14, outline:"none", fontFamily:"sans-serif", boxSizing:"border-box" };

  if (confirmed) return (
    <div style={{ textAlign:"center", padding:"80px 0" }}>
      <div style={{ fontSize:60, marginBottom:24 }}>🎀</div>
      <div style={{ color:C.noir, fontFamily:"'Cormorant Garamond', serif", fontSize:32, fontWeight:300, marginBottom:16 }}>Your order is confirmed!</div>
      <div style={{ color:C.mid, fontFamily:"'Raleway',sans-serif", fontSize:13, lineHeight:2 }}>
        Thank you for shopping with Posh Boutique.<br/>Your bag is being wrapped in luxury packaging just for you.<br/>
        <span style={{ color:C.accent }}>You're going to love how it makes you feel. 💕</span>
      </div>
    </div>
  );

  if (checkout) return (
    <div style={{ maxWidth:500, margin:"0 auto", textAlign:"center", padding:"60px 0" }}>
      <div style={{ fontSize:52, marginBottom:20 }}>🛍️</div>
      <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:30, marginBottom:12 }}>Secure Shopify Checkout</div>
      <div style={{ color:C.subtext, fontSize:14, fontFamily:"sans-serif", marginBottom:32, lineHeight:1.7 }}>Order total: <strong style={{ color:C.champagne }}>${total.toFixed(2)}</strong><br/>You're one step away from something beautiful.</div>
      <div style={{ background:C.bgAlt, border:`1px solid ${C.light}`, borderRadius:4, padding:"28px 32px", marginBottom:24 }}>
        <div style={{ color:C.muted, fontSize:9, letterSpacing:3, marginBottom:18, fontFamily:"'Raleway',sans-serif", textTransform:"uppercase" }}>🔒 SECURE PAYMENT OPTIONS</div>
        {["💳  Visa · Mastercard · Amex · Discover","🍎  Apple Pay · Google Pay","🛍️  Shop Pay · Afterpay · Klarna"].map((p,i)=>(
          <div key={i} style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:13, padding:"8px 0", borderBottom:i<2?`1px solid rgba(196,132,154,0.1)`:"none" }}>{p}</div>
        ))}
      </div>
      <button onClick={()=>{setCartItems([]);setConfirmed(true);}} style={{ width:"100%", background:`linear-gradient(135deg,${C.dustyRose},${C.roseGold})`, color:"#fff", border:"none", padding:"16px", borderRadius:12, fontSize:13, fontWeight:700, cursor:"pointer", letterSpacing:3, fontFamily:"sans-serif", boxShadow:`0 8px 24px rgba(196,132,154,0.35)` }}>
        CONFIRM & PAY · ${total.toFixed(2)}
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ textAlign:"center", marginBottom:40 }}>
        <div style={{ color:C.mauve, fontSize:11, letterSpacing:4, fontFamily:"sans-serif", marginBottom:8, textTransform:"uppercase" }}>Your Selection</div>
        <h1 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:38, margin:0, fontWeight:300 }}>Your Posh Cart</h1>
      </div>
      {cartItems.length===0 ? (
        <div style={{ textAlign:"center", color:C.muted, fontFamily:"sans-serif", padding:"60px 0", fontSize:15, fontStyle:"italic" }}>Your cart is empty — but it doesn't have to be. 🛍️</div>
      ) : (
        <div style={{ maxWidth:620, margin:"0 auto" }}>
          {cartItems.map(item=>(
            <div key={item.id} style={{ display:"flex", alignItems:"center", gap:18, padding:"20px 0", borderBottom:`1px solid ${C.light}` }}>
              <div style={{ fontSize:40, width:56, textAlign:"center" }}>{item.img}</div>
              <div style={{ flex:1 }}>
                <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:16, fontWeight:600 }}>{item.name}</div>
                <div style={{ color:C.muted, fontSize:11, fontFamily:"sans-serif", marginTop:2 }}>{item.brand} · {item.color}</div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <button onClick={()=>updateQty(item.id,-1)} style={{ background:"rgba(255,255,255,0.06)", border:`1px solid rgba(196,132,154,0.2)`, color:C.subtext, width:30, height:30, borderRadius:8, cursor:"pointer", fontSize:18 }}>−</button>
                <span style={{ color:C.champagne, fontFamily:"sans-serif", width:24, textAlign:"center", fontWeight:700 }}>{item.qty}</span>
                <button onClick={()=>updateQty(item.id,1)} style={{ background:"rgba(255,255,255,0.06)", border:`1px solid rgba(196,132,154,0.2)`, color:C.subtext, width:30, height:30, borderRadius:8, cursor:"pointer", fontSize:18 }}>+</button>
              </div>
              <div style={{ color:C.champagne, fontFamily:"sans-serif", fontWeight:700, minWidth:64, textAlign:"right", fontSize:16 }}>${(item.price*item.qty).toFixed(2)}</div>
            </div>
          ))}
          <div style={{ background:C.urgentBg, border:`1px solid rgba(212,80,106,0.2)`, borderRadius:10, padding:"12px 18px", marginTop:24, marginBottom:8, color:C.urgent, fontFamily:"sans-serif", fontSize:12, display:"flex", gap:10, alignItems:"center" }}>
            <span>⚠️</span><span>Popular items sell fast. Your cart is not reserved — complete checkout to secure your bags.</span>
          </div>
          <div style={{ marginTop:16, paddingTop:24, borderTop:`1px solid rgba(196,132,154,0.2)` }}>
            <div style={{ display:"flex", justifyContent:"space-between", color:C.champagne, fontFamily:"sans-serif", fontSize:22, fontWeight:700, marginBottom:28 }}>
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
            <button onClick={()=>setCheckout(true)} style={{ width:"100%", background:`linear-gradient(135deg,${C.dustyRose},${C.roseGold})`, color:"#fff", border:"none", padding:"17px", borderRadius:12, fontSize:13, fontWeight:700, cursor:"pointer", letterSpacing:3, fontFamily:"sans-serif", boxShadow:`0 10px 30px rgba(196,132,154,0.4)` }}>🛍️ CHECKOUT WITH SHOPIFY</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SHIPPING INFO ────────────────────────────────────────────────────────────
function ShippingPage() {
  return (
    <div style={{ maxWidth:720, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:52 }}>
        <div style={{ color:C.mauve, fontSize:11, letterSpacing:4, fontFamily:"sans-serif", marginBottom:10, textTransform:"uppercase" }}>Posh Boutique</div>
        <h1 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:40, margin:0, fontWeight:300 }}>Shipping Information</h1>
        <p style={{ color:C.muted, fontFamily:"sans-serif", fontSize:14, marginTop:14, lineHeight:1.8, fontStyle:"italic" }}>Because your bag deserves to arrive like the luxury it is.</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:36 }}>
        {[
          { icon:"📦", title:"Standard Shipping", desc:"All orders ship at a flat rate. We pack every order with care and get it on its way to you as quickly as possible." },
          { icon:"⚡", title:"Express Shipping", desc:"Can't wait? Express shipping available at checkout. Your dream bag delivered in 2–3 business days." },
          { icon:"🗓️", title:"Processing Time", desc:"Orders carefully packed within 1–3 business days. Weekend orders ship the following Monday." },
          { icon:"🚚", title:"Delivery Estimate", desc:"Standard shipping arrives in 7–9 business days. A tracking number is sent the moment it ships." },
          { icon:"💎", title:"Luxury Packaging", desc:"Wrapped in signature Posh tissue paper, a dust bag, and shipped in our branded gift box. Unboxing is part of the experience." },
          { icon:"📍", title:"Order Tracking", desc:"Track your order in real time via the Shopify link in your confirmation email. Your excitement is valid." },
        ].map((item,i)=>(
          <div key={i} style={{ background:"rgba(255,255,255,0.025)", border:`1px solid rgba(196,132,154,0.12)`, borderRadius:16, padding:"26px 22px" }}>
            <div style={{ fontSize:28, marginBottom:12 }}>{item.icon}</div>
            <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:17, marginBottom:8 }}>{item.title}</div>
            <div style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:13, lineHeight:1.8 }}>{item.desc}</div>
          </div>
        ))}
      </div>
      <div style={{ background:C.urgentBg, border:`1px solid rgba(212,80,106,0.3)`, borderRadius:16, padding:"36px 40px", textAlign:"center", marginBottom:24 }}>
        <div style={{ fontSize:36, marginBottom:14 }}>🚫</div>
        <h2 style={{ color:"#e88080", fontFamily:"'Cormorant Garamond', serif", fontSize:28, margin:"0 0 16px", fontWeight:400 }}>All Sales Are Final</h2>
        <p style={{ color:"#b06060", fontFamily:"sans-serif", fontSize:14, lineHeight:1.9, maxWidth:500, margin:"0 auto" }}>All purchases at Posh Boutique are <strong style={{ color:"#e88080" }}>final and non-refundable</strong>. We do not accept returns or exchanges. We encourage you to review all product details carefully and reach out with questions before purchasing.</p>
      </div>
      <div style={{ background:"rgba(255,255,255,0.02)", border:`1px solid rgba(196,132,154,0.1)`, borderRadius:14, padding:"28px 32px" }}>
        <h3 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:20, margin:"0 0 16px" }}>📋 Please Read Before You Order</h3>
        {["By completing your purchase you agree all sales are final and non-refundable.",
          "Have questions about color, size, or material? Please contact us before ordering — we're happy to help.",
          "In the rare case your order arrives damaged or incorrect, contact us within 48 hours with your order number and photos.",
          "Reach us at hello@poshboutique.com or our Contact page for any pre-purchase questions."
        ].map((t,i)=>(
          <div key={i} style={{ color:C.muted, fontFamily:"sans-serif", fontSize:13, lineHeight:1.9, display:"flex", gap:12, marginBottom:8 }}>
            <span style={{ color:C.accent }}>•</span><span>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CONTACT PAGE ─────────────────────────────────────────────────────────────
function ContactPage() {
  const [form, setForm] = useState({ name:"",email:"",subject:"",message:"" }); const [sent, setSent] = useState(false);
  const inputStyle = { width:"100%", background:"#fff", border:`1px solid ${C.light}`, color:C.noir, padding:"13px 16px", borderRadius:10, fontSize:14, outline:"none", fontFamily:"sans-serif", boxSizing:"border-box" };
  return (
    <div style={{ maxWidth:680, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:48 }}>
        <div style={{ color:C.mauve, fontSize:11, letterSpacing:4, fontFamily:"sans-serif", marginBottom:10, textTransform:"uppercase" }}>We'd love to hear from you</div>
        <h1 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:40, margin:0, fontWeight:300 }}>Contact Us</h1>
        <p style={{ color:C.subtext, fontFamily:"sans-serif", marginTop:12, fontSize:14, fontStyle:"italic" }}>Our team responds within 24 hours — usually much sooner.</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:44 }}>
        {[["📧","Email","hello@poshboutique.com"],["📱","Social / DM","@PoshBoutique"],["📍","Shipping","Ships Nationwide"]].map(([icon,label,val],i)=>(
          <div key={i} style={{ background:"rgba(255,255,255,0.025)", border:`1px solid rgba(196,132,154,0.12)`, borderRadius:14, padding:"20px 16px", textAlign:"center" }}>
            <div style={{ fontSize:26, marginBottom:8 }}>{icon}</div>
            <div style={{ color:C.muted, fontSize:10, letterSpacing:2, fontFamily:"sans-serif", marginBottom:4, textTransform:"uppercase" }}>{label}</div>
            <div style={{ color:C.champagne, fontFamily:"sans-serif", fontSize:12 }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ background:"rgba(255,255,255,0.025)", border:`1px solid rgba(196,132,154,0.15)`, borderRadius:20, padding:"36px 40px" }}>
        {sent ? (
          <div style={{ textAlign:"center", padding:"32px 0" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>💌</div>
            <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:26 }}>Message Received!</div>
            <div style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:14, marginTop:12 }}>We'll be in touch within 24 hours. Thank you for reaching out. 💕</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            {[{label:"Full Name",key:"name",type:"text",ph:"Your name"},{label:"Email Address",key:"email",type:"email",ph:"your@email.com"},{label:"Subject",key:"subject",type:"text",ph:"What's on your mind?"}].map(f=>(
              <div key={f.key}>
                <label style={{ color:C.muted, fontSize:11, letterSpacing:2, fontFamily:"sans-serif", display:"block", marginBottom:6, textTransform:"uppercase" }}>{f.label}</label>
                <input type={f.type} placeholder={f.ph} value={form[f.key]} onChange={e=>setForm({...form,[f.key]:e.target.value})} style={inputStyle}/>
              </div>
            ))}
            <div>
              <label style={{ color:C.muted, fontSize:11, letterSpacing:2, fontFamily:"sans-serif", display:"block", marginBottom:6, textTransform:"uppercase" }}>Message</label>
              <textarea placeholder="How can we help you?" value={form.message} onChange={e=>setForm({...form,message:e.target.value})} rows={5} style={{...inputStyle, resize:"vertical"}}/>
            </div>
            <button onClick={()=>setSent(true)} style={{ background:`linear-gradient(135deg,${C.dustyRose},${C.roseGold})`, color:"#fff", border:"none", padding:14, borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer", letterSpacing:3, fontFamily:"sans-serif", boxShadow:`0 8px 24px rgba(196,132,154,0.3)` }}>SEND MESSAGE</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── RESTOCK PAGE ─────────────────────────────────────────────────────────────
function RestockPage() {
  const [form, setForm] = useState({ name:"",email:"",phone:"",item:"" }); const [done, setDone] = useState(false);
  const wishlist = ["Chanel Classic Flap (Black Caviar)","Hermès Birkin 30","LV Neverfull MM Monogram","Gucci Dionysus GG","Dior Lady Dior Medium","Bottega Veneta Jodie","Celine Luggage Micro","Prada Re-Edition 2000"];
  const inputStyle = { width:"100%", background:"#fff", border:`1px solid ${C.light}`, color:C.noir, padding:"13px 16px", borderRadius:10, fontSize:14, outline:"none", fontFamily:"sans-serif", boxSizing:"border-box" };
  return (
    <div style={{ maxWidth:640, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:48 }}>
        <div style={{ fontSize:44, marginBottom:14 }}>🔔</div>
        <h1 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:40, margin:0, fontWeight:300 }}>Restock Alerts</h1>
        <p style={{ color:C.subtext, fontFamily:"sans-serif", marginTop:12, lineHeight:1.8, fontStyle:"italic" }}>You found the one. Don't lose it forever.<br/>Be the <em>first</em> to know the moment it's back.</p>
      </div>
      <div style={{ background:"rgba(255,255,255,0.025)", border:`1px solid rgba(196,132,154,0.15)`, borderRadius:20, padding:"36px 40px" }}>
        {done ? (
          <div style={{ textAlign:"center", padding:"24px 0" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
            <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:24 }}>You're on the list!</div>
            <div style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:14, marginTop:12, lineHeight:1.8 }}>The moment it's back, you'll be the first to know — via text and email. Don't wait too long when it hits. 💕</div>
            <button onClick={()=>setDone(false)} style={{ marginTop:20, background:"rgba(255,255,255,0.05)", border:`1px solid rgba(196,132,154,0.25)`, color:C.subtext, padding:"10px 24px", borderRadius:8, cursor:"pointer", fontFamily:"sans-serif", fontSize:12 }}>Set Another Alert</button>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <h2 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", margin:"0 0 4px", fontWeight:400 }}>Set Your Alert</h2>
            <p style={{ color:C.muted, fontFamily:"sans-serif", fontSize:13, margin:0, fontStyle:"italic" }}>We'll text and email you the second it's available. First come, first served.</p>
            {[{label:"Full Name",key:"name",type:"text",ph:"Your name"},{label:"Email Address",key:"email",type:"email",ph:"your@email.com"},{label:"Cell Phone (SMS)",key:"phone",type:"tel",ph:"+1 (555) 000-0000"}].map(f=>(
              <div key={f.key}>
                <label style={{ color:C.muted, fontSize:11, letterSpacing:2, fontFamily:"sans-serif", display:"block", marginBottom:6, textTransform:"uppercase" }}>{f.label}</label>
                <input type={f.type} placeholder={f.ph} value={form[f.key]} onChange={e=>setForm({...form,[f.key]:e.target.value})} style={inputStyle}/>
              </div>
            ))}
            <div>
              <label style={{ color:C.muted, fontSize:11, letterSpacing:2, fontFamily:"sans-serif", display:"block", marginBottom:6, textTransform:"uppercase" }}>Item You Want</label>
              <select value={form.item} onChange={e=>setForm({...form,item:e.target.value})} style={{...inputStyle, background:C.deepWine, color:form.item?C.champagne:C.muted}}>
                <option value="">Choose your bag...</option>
                {wishlist.map(w=><option key={w}>{w}</option>)}
                <option>Other — I'll describe below</option>
              </select>
            </div>
            <button onClick={()=>setDone(true)} style={{ background:`linear-gradient(135deg,${C.dustyRose},${C.roseGold})`, color:"#fff", border:"none", padding:15, borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer", letterSpacing:3, fontFamily:"sans-serif", boxShadow:`0 8px 24px rgba(196,132,154,0.3)` }}>🔔 NOTIFY ME WHEN IT'S BACK</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LIVE SALE PAGE ───────────────────────────────────────────────────────────
function LiveSalePage() {
  return (
    <div style={{ maxWidth:800, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:52 }}>
        <div style={{ fontSize:48, marginBottom:14 }}>📺</div>
        <h1 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:42, margin:0, fontWeight:300 }}>Live Sales</h1>
        <p style={{ color:C.subtext, fontFamily:"sans-serif", marginTop:12, lineHeight:1.9, fontStyle:"italic" }}>The most fun you'll have shopping. Join us on Facebook Live for exclusive drops,<br/>flash prices, and first picks on new inventory.</p>
      </div>
      <h2 style={{ color:C.subtext, fontFamily:"'Cormorant Garamond', serif", fontSize:22, marginBottom:20, fontWeight:400 }}>📅 Upcoming Live Sales</h2>
      <div style={{ display:"flex", flexDirection:"column", gap:16, marginBottom:52 }}>
        {LIVE_SALES.map((sale,i)=>(
          <div key={i} style={{ background:i===0?`linear-gradient(135deg,rgba(196,132,154,0.1),rgba(212,168,83,0.05))`:"rgba(255,255,255,0.025)", border:`1px solid ${i===0?"rgba(196,132,154,0.4)":"rgba(196,132,154,0.12)"}`, borderRadius:16, padding:"24px 28px", display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
            {i===0 && <div style={{ background:C.urgent, color:"#fff", borderRadius:20, padding:"4px 12px", fontSize:10, fontWeight:700, fontFamily:"sans-serif", whiteSpace:"nowrap", letterSpacing:1 }}>NEXT LIVE</div>}
            <div style={{ flex:1 }}>
              <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:20, marginBottom:4 }}>{sale.theme}</div>
              <div style={{ color:C.muted, fontFamily:"sans-serif", fontSize:12 }}>📅 {sale.date} at {sale.time} · {sale.platform}</div>
              {sale.watching>0 && <div style={{ color:C.accent, fontFamily:"sans-serif", fontSize:11, marginTop:4, fontWeight:700 }}>🔴 {sale.watching} women watching right now</div>}
            </div>
            <a href="https://facebook.com" target="_blank" rel="noopener" style={{ background:"#1877F2", color:"#fff", textDecoration:"none", padding:"10px 20px", borderRadius:8, fontSize:11, fontWeight:700, fontFamily:"sans-serif", letterSpacing:1, whiteSpace:"nowrap" }}>📘 SET REMINDER</a>
          </div>
        ))}
      </div>
      <div style={{ background:"rgba(255,255,255,0.025)", border:`1px solid rgba(196,132,154,0.15)`, borderRadius:20, padding:"40px 44px" }}>
        <h2 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:28, marginTop:0, marginBottom:8, fontWeight:400 }}>How to Claim During a Live Sale</h2>
        <p style={{ color:C.muted, fontFamily:"sans-serif", fontSize:13, marginBottom:32, fontStyle:"italic", lineHeight:1.7 }}>It moves fast. Here's everything you need to know so you never lose your bag to someone else.</p>
        {[
          { n:"1", title:"Follow & Turn On Notifications", desc:"Follow @PoshBoutique on Facebook and enable Live notifications. You'll get an alert the moment we go live — don't miss the opening minutes." },
          { n:"2", title:"Join the Live at Showtime", desc:"Hop in early. Early viewers get first look at inventory before it's shown on screen. The energy is contagious — bring a friend!" },
          { n:"3", title:"Comment CLAIM to Secure Your Bag", desc:'The moment you see your bag, type "CLAIM" in the comments immediately. First comment wins. Be fast.' },
          { n:"4", title:"Watch for Your Confirmation", desc:"We'll reply to your comment confirming your claim. You'll receive a Shopify invoice link via Messenger within 15 minutes." },
          { n:"5", title:"Complete Payment Within 30 Minutes", desc:"Your bag is held for 30 minutes. Pay via the invoice link or it releases to the next person. We accept all major cards, Apple Pay & more." },
          { n:"6", title:"We Pack It With Love & Ship Fast 🎀", desc:"Once payment clears, your order ships within 1–3 business days in our signature luxury packaging. Get ready for the best unboxing of your life." },
        ].map((s,i)=>(
          <div key={i} style={{ display:"flex", gap:20, marginBottom:28 }}>
            <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,${C.dustyRose},${C.roseGold})`, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:14, fontWeight:700, fontFamily:"sans-serif", flexShrink:0 }}>{s.n}</div>
            <div>
              <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:17, marginBottom:5 }}>{s.title}</div>
              <div style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:13, lineHeight:1.8 }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ABOUT PAGE ───────────────────────────────────────────────────────────────
function AboutPage() {
  return (
    <div style={{ maxWidth:740, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:60 }}>
        <div style={{ fontSize:52, marginBottom:16 }}>💕</div>
        <div style={{ color:C.mauve, fontSize:11, letterSpacing:6, fontFamily:"sans-serif", marginBottom:12, textTransform:"uppercase" }}>The Posh Story</div>
        <h1 style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:46, margin:0, fontWeight:300 }}>
          Built for <em style={{ color:C.accent, fontStyle:"italic" }}>Her.</em>
        </h1>
      </div>
      <div style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:15, lineHeight:2.1, marginBottom:52 }}>
        <p style={{ marginBottom:20 }}><em style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:18 }}>Posh Boutique was created for a very specific kind of woman.</em></p>
        <p style={{ marginBottom:20 }}>She walks into a room and people notice — not because she's loud, but because she's <em>intentional</em>. She knows that the right bag isn't just an accessory. It's a statement. It's self-respect made visible. It's the silent confidence of a woman who invests in herself.</p>
        <p style={{ marginBottom:20 }}>We built Posh Boutique because we believe every woman deserves to carry something extraordinary — without paying department store prices or compromising on quality. We hand-select every single piece in our collection, asking one question: <em style={{ color:C.champagne }}>"Would a woman feel powerful carrying this?"</em></p>
        <p>If the answer is yes, it earns a place in the boutique. If not, it doesn't. That's the Posh standard — and our customers feel the difference every single time they step out.</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:48 }}>
        {[
          { icon:"🌟", title:"The Posh Standard", desc:"Every bag is hand-curated. We don't stock anything we wouldn't carry ourselves. Quality is non-negotiable." },
          { icon:"💎", title:"Luxury Made Accessible", desc:"You shouldn't have to choose between quality and your budget. Posh makes sure you never have to." },
          { icon:"🤝", title:"The Posh Community", desc:"Our customers aren't just shoppers — they're the Posh family. Over 1,000 strong and growing daily." },
          { icon:"📦", title:"White-Glove Service", desc:"Every order ships in our signature packaging because the experience matters from click to unboxing." },
        ].map((v,i)=>(
          <div key={i} style={{ background:"rgba(255,255,255,0.025)", border:`1px solid rgba(196,132,154,0.12)`, borderRadius:16, padding:"28px 24px" }}>
            <div style={{ fontSize:30, marginBottom:12 }}>{v.icon}</div>
            <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:18, marginBottom:8 }}>{v.title}</div>
            <div style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:13, lineHeight:1.8 }}>{v.desc}</div>
          </div>
        ))}
      </div>
      <div style={{ background:`linear-gradient(135deg,rgba(196,132,154,0.1),rgba(212,168,83,0.05))`, border:`1px solid rgba(196,132,154,0.25)`, borderRadius:20, padding:"44px 48px", textAlign:"center" }}>
        <div style={{ color:C.champagne, fontFamily:"'Cormorant Garamond', serif", fontSize:26, fontStyle:"italic", marginBottom:12, lineHeight:1.4 }}>"I shop with Posh Boutique and nothing else comes close."</div>
        <div style={{ color:C.muted, fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:0.5, letterSpacing:3, textTransform:"uppercase" }}>— The Posh Community</div>
        <div style={{ width:50, height:1, background:`linear-gradient(to right,transparent,${C.dustyRose},transparent)`, margin:"22px auto" }}/>
        <div style={{ color:C.subtext, fontFamily:"sans-serif", fontSize:14, lineHeight:1.8 }}>
          Join us: <strong style={{ color:C.champagne }}>@PoshBoutique</strong> on Facebook & Instagram
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── MOBILE MENU COMPONENT ────────────────────────────────────────────────────
function MobileMenu({ setPage, setMegaNav, setMobileMenuOpen, C }) {
  const [openItem, setOpenItem] = useState(null);

  const navItems = [
    {
      label: "Best Sellers",
      direct: {main:"bags",sub:"bags",inner:"bestSellers"},
    },
    {
      label: "New",
      sub: [
        { label:"Bags", nav:{main:"new",sub:"bags",inner:"bestSellers"} },
        { label:"Accessories", nav:{main:"new",sub:"accessories",inner:"bestSellers"} },
      ]
    },
    {
      label: "Bags",
      sub: [
        { label:"Belt Bags", nav:{main:"bags",sub:"bags",inner:"bestSellers"} },
        { label:"Handbags", nav:{main:"bags",sub:"bags",inner:"bestSellers"} },
        { label:"Crossbody", nav:{main:"bags",sub:"bags",inner:"bestSellers"} },
        { label:"Tote Bags", nav:{main:"bags",sub:"bags",inner:"bestSellers"} },
        { label:"⚡ Lightning Deals", nav:{main:"bags",sub:"bags",inner:"lightningDeals"} },
      ]
    },
    {
      label: "Accessories",
      sub: [
        { label:"Wallets", nav:{main:"accessories",sub:"accessories",inner:"bestSellers"} },
        { label:"Card Holders", nav:{main:"accessories",sub:"accessories",inner:"bestSellers"} },
        { label:"Jewelry", nav:{main:"accessories",sub:"accessories",inner:"bestSellers"} },
        { label:"⚡ Lightning Deals", nav:{main:"accessories",sub:"accessories",inner:"lightningDeals"} },
      ]
    },
  ];

  return (
    <div style={{ flex:1, overflowY:"auto" }}>
      {navItems.map((item, i) => (
        <div key={i}>
          <button
            onClick={()=>{
              if (item.direct) { setMegaNav(item.direct); setPage("shop"); setMobileMenuOpen(false); }
              else { setOpenItem(openItem === item.label ? null : item.label); }
            }}
            style={{ display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", background:"none", border:"none", borderBottom:`1px solid ${C.light}`, padding:"20px 20px", cursor:"pointer", textAlign:"left" }}
          >
            <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:30, fontWeight:300, color:C.noir, letterSpacing:0.5 }}>{item.label}</span>
            {item.sub && (
              <span style={{ fontSize:18, color:C.mid, transition:"transform 0.2s", display:"inline-block", transform: openItem===item.label ? "rotate(90deg)" : "rotate(0deg)" }}>→</span>
            )}
          </button>
          {/* Sub items */}
          {item.sub && openItem === item.label && (
            <div style={{ background:C.bgAlt, borderBottom:`1px solid ${C.light}` }}>
              {item.sub.map((sub, j) => (
                <button key={j}
                  onClick={()=>{ setMegaNav(sub.nav); setPage("shop"); setMobileMenuOpen(false); }}
                  style={{ display:"block", width:"100%", background:"none", border:"none", borderBottom:`1px solid ${C.light}`, padding:"14px 36px", cursor:"pointer", textAlign:"left", fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:2.5, textTransform:"uppercase", color:C.mid }}
                >{sub.label}</button>
              ))}
            </div>
          )}
        </div>
      ))}
      {/* Bottom pages */}
      <div>
        {[{id:"restock",label:"Restock"},{id:"about",label:"About"},{id:"contact",label:"Contact"}].map(n=>(
          <button key={n.id} onClick={()=>{ setPage(n.id); setMobileMenuOpen(false); }} style={{ display:"block", width:"100%", background:"none", border:"none", borderBottom:`1px solid ${C.light}`, padding:"16px 20px", cursor:"pointer", textAlign:"left", fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:3, textTransform:"uppercase", color:C.mid }}>{n.label}</button>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const [unlocked, setUnlocked] = useState(false);
  const [page, setPage] = useState("shop");
  const [cart, setCart] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showCartRecovery, setShowCartRecovery] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [topBarHeight, setTopBarHeight] = useState(80);
  const [megaNav, setMegaNav] = useState(null);
  const [cartDismissed, setCartDismissed] = useState(false);
  const recoveryTimer = useRef(null);
  const cartCount = cart.reduce((s,i)=>s+i.qty,0);

  useEffect(() => { if (unlocked) { const alreadySigned = localStorage.getItem('posh_signed_up'); if (!alreadySigned) { const t=setTimeout(()=>setShowPopup(true),2800); return ()=>clearTimeout(t); } } }, [unlocked]);
  useEffect(() => {
    if (!cartDismissed && cart.length>0 && page!=="cart") { clearTimeout(recoveryTimer.current); recoveryTimer.current=setTimeout(()=>setShowCartRecovery(true),15000); }
    else { setShowCartRecovery(false); }
    return ()=>clearTimeout(recoveryTimer.current);
  }, [cart,page,cartDismissed]);

  const addToCart = (product) => setCart(prev => {
    const ex=prev.find(i=>i.id===product.id);
    return ex ? prev.map(i=>i.id===product.id?{...i,qty:i.qty+1}:i) : [...prev,{...product,qty:1}];
  });

  if (!unlocked) return <PasswordGate onUnlock={()=>setUnlocked(true)}/>;

  const NAV = [
    {id:"shop",label:"Shop"}, {id:"livesale",label:"Live Sales"},
    {id:"restock",label:"Restock Alerts"}, {id:"shipping",label:"Shipping Info"},
    {id:"contact",label:"Contact"}, {id:"about",label:"About"},
  ];

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.noir }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Raleway:wght@300;400;600;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        ::-webkit-scrollbar { width:4px; }
        ::-webkit-scrollbar-track { background:${C.bg}; }
        ::-webkit-scrollbar-thumb { background:${C.light}; border-radius:4px; }
        input::placeholder, textarea::placeholder { color:${C.muted}; }
        select option { background:#fff; color:${C.noir}; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        button:focus { outline:none; }
        body { overflow-x: hidden; }

        /* ── MOBILE ───────────────────────────────────────────────── */
        @media (max-width: 768px) {
          .footer-desktop-cols { display:none !important; }
          .footer-mobile-cols { display:grid !important; }
          .announce-bar { display:none !important; }
          .trust-bar { display:none !important; }
          .hdr-left { display:none !important; }
          .hdr-right { display:none !important; }
          .hdr-mobile-left { display:flex !important; }
          .hdr-mobile-right { display:flex !important; }
          .hdr-wrap { height:56px !important; padding:0 20px !important; }
          .hdr-logo { font-size:20px !important; letter-spacing:5px !important; }
          .main-wrap { padding:0 16px 60px !important; }
          .hero-wrap { padding:32px 0 24px !important; margin-bottom:20px !important; }
          .hero-label { display:none !important; }
          .hero-sub { font-size:16px !important; line-height:1.8 !important; }
          .products-grid { display:none !important; }
          .mob-hide { display:none !important; }
          .footer-wrap { padding:28px 16px 20px !important; }
          .footer-cols { grid-template-columns:1fr 1fr !important; gap:20px !important; }
          .footer-bottom { flex-direction:column !important; gap:8px !important; text-align:center !important; }
        }
        @media (max-width: 480px) {
          .products-grid { grid-template-columns:1fr 1fr !important; }
        }
          .cart-grid { grid-template-columns:1fr !important; }
          .signup-popup { padding:32px 24px !important; margin:16px !important; }
          .logo-text { font-size:22px !important; letter-spacing:4px !important; }
        }
        @media (max-width: 480px) {
          .product-grid { grid-template-columns:1fr !important; }
          .footer-grid { grid-template-columns:1fr !important; }
        }
      `}</style>

      {/* ANNOUNCEMENT BAR */}
      <div className="announce-bar" style={{ background:C.noir, borderBottom:`1px solid rgba(0,0,0,0.06)`, padding:"10px 0", textAlign:"center" }}>
        <span style={{ color:"#c8c0b8", fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:2.5, textTransform:"uppercase" }}>
          Join 1,000+ women who choose Posh &nbsp;&nbsp;·&nbsp;&nbsp; Sign up for <span style={{ color:"#e8d5b0" }}>10% off</span> your first order
        </span>
      </div>

      {/* MOBILE MENU OVERLAY */}
      {mobileMenuOpen && (
        <div style={{ position:"fixed", inset:0, background:"#fff", zIndex:1000, display:"flex", flexDirection:"column" }}>
          {/* Top bar */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", padding:"16px 20px", borderBottom:`1px solid ${C.light}` }}>
            <button onClick={()=>setMobileMenuOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:22, color:C.noir, fontFamily:"'Raleway',sans-serif" }}>✕</button>
          </div>
          {/* Search */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"20px 20px 16px", borderBottom:`1px solid ${C.light}` }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.mid} strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input value={mobileSearch} onChange={e=>setMobileSearch(e.target.value)} placeholder="TYPE TO SEARCH" style={{ border:"none", outline:"none", fontFamily:"'Raleway',sans-serif", fontSize:11, letterSpacing:3, color:C.noir, width:"100%", background:"none", textTransform:"uppercase" }}/>
          </div>
          {/* Nav links */}
          <MobileMenu setPage={setPage} setMegaNav={setMegaNav} setMobileMenuOpen={setMobileMenuOpen} C={C} />
        </div>
      )}

      {/* HEADER — exact Polène layout: left nav | center logo | right utilities */}
      <header style={{ position:"sticky", top:0, zIndex:500, background:C.header, backdropFilter:"blur(20px)" }}
        onMouseLeave={()=>setMegaOpen(false)}
      >
        <div className="hdr-wrap" style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", alignItems:"center", height:72, padding:"0 clamp(16px,4vw,48px)", borderBottom:`1px solid ${C.light}` }}>

          {/* LEFT NAV — desktop only */}
          <nav className="hdr-left" style={{ display:"flex", alignItems:"center", gap:28 }}>
            {[
              {id:"shop", label:"Best Sellers", mega:false, nav:{main:"bags",sub:"bags",inner:"bestSellers"}},
              {id:"shop", label:"New", mega:true, directNav:{main:"new",sub:"all",inner:"bestSellers"}},
              {id:"shop", label:"Bags", mega:true},
              {id:"shop", label:"Accessories", mega:true},
            ].map((item,i)=>{
              const isActive = megaOpen === item.label;
              return (
                <button key={i}
                  onClick={()=>{
                    if(!item.mega){ setMegaNav(item.nav); setPage("shop"); setMegaOpen(false); }
                    else if(item.directNav){ setMegaNav(item.directNav); setPage("shop"); setMegaOpen(false); }
                    else { setMegaOpen(isActive ? false : item.label); }
                  }}
                  onMouseEnter={()=>{ if(item.mega) setMegaOpen(item.label); }}
                  style={{ background:"none", border:"none", borderBottom: isActive ? `1px solid ${C.noir}` : "1px solid transparent", padding:"0", height:72, cursor:"pointer", fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontWeight:400, color: isActive ? C.noir : C.mid, transition:"color 0.2s", whiteSpace:"nowrap" }}
                  onMouseEnter={e=>{ e.currentTarget.style.color=C.noir; if(item.mega) setMegaOpen(item.label); }}
                  onMouseLeave={e=>{ if(!isActive) e.currentTarget.style.color=C.mid; }}
                >{item.label.toUpperCase()}</button>
              );
            })}
          </nav>

          {/* MOBILE LEFT — hamburger + search (hidden on desktop via CSS) */}
          <div className="hdr-mobile-left" style={{ display:"none", alignItems:"center", gap:16 }}>
            <button onClick={()=>setMobileMenuOpen(true)} style={{ background:"none", border:"none", cursor:"pointer", padding:0, display:"flex", flexDirection:"column", gap:5 }}>
              <span style={{ display:"block", width:22, height:1.5, background:C.noir }}/>
              <span style={{ display:"block", width:22, height:1.5, background:C.noir }}/>
              <span style={{ display:"block", width:22, height:1.5, background:C.noir }}/>
            </button>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.mid} strokeWidth="1.5" style={{ cursor:"pointer" }}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </div>

          {/* CENTER — logo */}
          <div onClick={()=>{ setPage("shop"); setMegaOpen(false); }} style={{ cursor:"pointer", userSelect:"none", display:"inline-block", padding:"0 clamp(8px,2vw,48px)" }}>
            <div className="hdr-logo" style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(16px,3vw,36px)", fontWeight:400, letterSpacing:"clamp(3px,1vw,8px)", color:C.noir, textTransform:"uppercase", lineHeight:1, whiteSpace:"nowrap" }}>
              Posh Boutique
            </div>
          </div>

          {/* RIGHT — desktop nav + cart */}
          <div className="hdr-right" style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:"clamp(6px,1.5vw,28px)" }}>
            {[{id:"livesale",label:"Live Sales"},{id:"restock",label:"Restock"},{id:"about",label:"About"},{id:"contact",label:"Contact"}].map(n=>(
              <button key={n.id} onClick={()=>{ setPage(n.id); setMegaOpen(false); }} style={{ background:"none", border:"none", borderBottom: page===n.id ? `1px solid ${C.noir}` : "1px solid transparent", color:page===n.id?C.noir:C.mid, padding:"0", height:72, cursor:"pointer", fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontWeight:400, transition:"color 0.2s", whiteSpace:"nowrap" }}
                onMouseEnter={e=>e.currentTarget.style.color=C.noir}
                onMouseLeave={e=>{ if(page!==n.id) e.currentTarget.style.color=C.mid; }}
              >{n.label}</button>
            ))}
            <button onClick={()=>{ setPage("cart"); setMegaOpen(false); }} style={{ background:"none", border:"none", borderBottom: page==="cart" ? `1px solid ${C.noir}` : "1px solid transparent", color:page==="cart"?C.noir:C.mid, padding:"0", height:72, cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontWeight:400, transition:"color 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.color=C.noir}
              onMouseLeave={e=>{ if(page!=="cart") e.currentTarget.style.color=C.mid; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              <span>({cartCount})</span>
            </button>
          </div>

          {/* MOBILE RIGHT — heart + cart only */}
          <div className="hdr-mobile-right" style={{ display:"none", alignItems:"center", justifyContent:"flex-end", gap:18 }}>
            <div style={{ display:"flex", alignItems:"center", gap:3 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.noir} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              <span style={{ fontSize:13, color:C.noir, fontFamily:"'Cormorant Garamond',serif" }}>0</span>
            </div>
            <button onClick={()=>{ setPage("cart"); setMegaOpen(false); }} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:3, padding:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.noir} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              <span style={{ fontSize:13, color:C.noir, fontFamily:"'Cormorant Garamond',serif" }}>{cartCount}</span>
            </button>
          </div>
        </div>

        {/* MEGA DROPDOWN PANEL — full width, appears below header */}
        {megaOpen && (
          <div style={{ position:"absolute", left:0, right:0, background:C.header, backdropFilter:"blur(20px)", borderBottom:`1px solid ${C.light}`, zIndex:499, padding:"clamp(16px,4vw,32px) clamp(16px,6vw,80px) clamp(16px,3vw,36px)", className:"mega-wrap" }}
            onMouseEnter={()=>setMegaOpen(megaOpen)}
            onMouseLeave={()=>setMegaOpen(false)}
          >
            <div className="mega-inner" style={{ maxWidth:900, display:"flex", gap:80 }}>
              {megaOpen === "New" && (
                <>
                  <div>
                    {[{label:"Bags", nav:{main:"new",sub:"bags",inner:"bestSellers"}},{label:"Accessories", nav:{main:"new",sub:"accessories",inner:"bestSellers"}}].map(item=>(
                      <button key={item.label} onClick={()=>{ setPage("shop"); setMegaNav(item.nav); setMegaOpen(false); }} style={{ display:"block", background:"none", border:"none", color:C.mid, fontFamily:"'Raleway',sans-serif", fontSize:9, fontWeight:400, letterSpacing:2.5, textTransform:"uppercase", padding:"8px 0", cursor:"pointer", transition:"color 0.2s", textAlign:"left" }}
                        onMouseEnter={e=>e.currentTarget.style.color=C.noir}
                        onMouseLeave={e=>e.currentTarget.style.color=C.mid}
                      >{item.label.toUpperCase()}</button>
                    ))}
                  </div>
                </>
              )}
              {megaOpen === "Bags" && (
                <div>
                  {["Belt bags","Handbags","Crossbody","Tote bags"].map(label=>(
                    <button key={label} onClick={()=>{ setPage("shop"); setMegaNav({main:"bags",sub:"bags",inner:"bestSellers"}); setMegaOpen(false); }} style={{ display:"block", background:"none", border:"none", color:C.mid, fontFamily:"'Raleway',sans-serif", fontSize:9, fontWeight:400, letterSpacing:2.5, padding:"8px 0", cursor:"pointer", transition:"color 0.2s", textAlign:"left", textTransform:"uppercase" }}
                      onMouseEnter={e=>e.currentTarget.style.color=C.noir}
                      onMouseLeave={e=>e.currentTarget.style.color=C.mid}
                    >{label.toUpperCase()}</button>
                  ))}
                  <div style={{ borderTop:`1px solid rgba(196,132,154,0.15)`, margin:"12px 0 8px" }}/>
                  <button onClick={()=>{ setPage("shop"); setMegaNav({main:"bags",sub:"bags",inner:"lightningDeals"}); setMegaOpen(false); }} style={{ display:"block", background:"none", border:"none", color:C.urgent, fontFamily:"'Raleway',sans-serif", fontSize:9, fontWeight:400, letterSpacing:2.5, padding:"8px 0", cursor:"pointer", transition:"color 0.2s", textAlign:"left", textTransform:"uppercase" }}
                    onMouseEnter={e=>e.currentTarget.style.color="#ff6b8a"}
                    onMouseLeave={e=>e.currentTarget.style.color=C.urgent}
                  >⚡ Lightning Deals</button>
                </div>
              )}
              {megaOpen === "Accessories" && (
                <div>
                  {[
                    {label:"Wallets", inner:"bestSellers"},
                    {label:"Card Holders", inner:"bestSellers"},
                    {label:"Jewelry", inner:"bestSellers"},
                  ].map(item=>(
                    <button key={item.label} onClick={()=>{ setPage("shop"); setMegaNav({main:"accessories",sub:"accessories",inner:item.inner}); setMegaOpen(false); }} style={{ display:"block", background:"none", border:"none", color:C.mid, fontFamily:"'Raleway',sans-serif", fontSize:9, fontWeight:400, letterSpacing:2.5, padding:"8px 0", cursor:"pointer", transition:"color 0.2s", textAlign:"left", textTransform:"uppercase" }}
                      onMouseEnter={e=>e.currentTarget.style.color=C.noir}
                      onMouseLeave={e=>e.currentTarget.style.color=C.mid}
                    >{item.label.toUpperCase()}</button>
                  ))}
                  <div style={{ borderTop:`1px solid rgba(196,132,154,0.15)`, margin:"12px 0 8px" }}/>
                  <button onClick={()=>{ setPage("shop"); setMegaNav({main:"accessories",sub:"accessories",inner:"lightningDeals"}); setMegaOpen(false); }} style={{ display:"block", background:"none", border:"none", color:C.urgent, fontFamily:"'Raleway',sans-serif", fontSize:9, fontWeight:400, letterSpacing:2.5, padding:"8px 0", cursor:"pointer", transition:"color 0.2s", textAlign:"left", textTransform:"uppercase" }}
                    onMouseEnter={e=>e.currentTarget.style.color="#ff6b8a"}
                    onMouseLeave={e=>e.currentTarget.style.color=C.urgent}
                  >⚡ Lightning Deals</button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <TrustBar/>

      {/* MAIN */}
      <main className="main-wrap" style={{ maxWidth:1200, margin:"0 auto", padding:"0 clamp(16px,4vw,48px) 100px", animation:"fadeSlideIn 0.5s ease" }}>
        {page==="shop"     && <ShopPage onAddToCart={addToCart} megaNav={megaNav} clearMegaNav={()=>setMegaNav(null)}/>}
        {page==="cart"     && <CartPage cartItems={cart} setCartItems={setCart}/>}
        {page==="livesale" && <LiveSalePage/>}
        {page==="restock"  && <RestockPage/>}
        {page==="shipping" && <ShippingPage/>}
        {page==="contact"  && <ContactPage/>}
        {page==="about"    && <AboutPage/>}
      </main>

      {/* FOOTER */}
      <footer style={{ borderTop:`1px solid ${C.light}`, background:C.bgAlt, padding:"clamp(24px,4vw,56px) clamp(16px,4vw,48px) clamp(16px,3vw,36px)", className:"footer-wrap" }}>
        <div style={{ maxWidth:1140, margin:"0 auto" }}>
          {/* DESKTOP FOOTER — unchanged */}
          <div className="footer-desktop-cols" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:"clamp(16px,3vw,48px)", marginBottom:40 }}>
            <div>
              <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:18, fontWeight:400, letterSpacing:5, color:C.noir, marginBottom:16 }}>POSH BOUTIQUE</div>
              <p style={{ color:C.mid, fontFamily:"'Raleway',sans-serif", fontSize:12, lineHeight:2, maxWidth:240, fontWeight:300 }}>For the woman who carries herself with intention — and her bag the same way.</p>
              <div style={{ display:"flex", gap:12, marginTop:20 }}>
                <a href="mailto:PoshboutiqueNY@gmail.com" onClick={e=>{e.preventDefault();window.open("mailto:PoshboutiqueNY@gmail.com","_self");}} title="Email us" style={{ width:36, height:36, borderRadius:"50%", background:"rgba(196,132,154,0.08)", border:`1px solid rgba(196,132,154,0.2)`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", textDecoration:"none" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c4849a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/xoposhluxe" onClick={e=>{e.preventDefault();window.open("https://www.instagram.com/xoposhluxe","_blank");}} target="_blank" rel="noopener" title="Instagram @xoposhluxe" style={{ width:36, height:36, borderRadius:"50%", background:"rgba(196,132,154,0.08)", border:`1px solid rgba(196,132,154,0.2)`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", textDecoration:"none" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c4849a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1" fill="#c4849a" stroke="none"/>
                  </svg>
                </a>
              </div>
            </div>
            {[
              { title:"Shop", links:["Best Sellers","Back In Stock","New Arrivals","Lightning Deals"] },
              { title:"Boutique", links:["About Us","Live Sales","Restock Alerts","Contact"] },
              { title:"Support", links:["Shipping Info","All Sales Final","FAQ","Privacy Policy"] },
            ].map((col,i)=>(
              <div key={i}>
                <div style={{ color:C.noir, fontSize:9, letterSpacing:4, fontFamily:"'Raleway',sans-serif", marginBottom:20, textTransform:"uppercase", fontWeight:600 }}>{col.title}</div>
                {col.links.map(l=>(
                  <div key={l} style={{ color:C.muted, fontFamily:"sans-serif", fontSize:12, marginBottom:12, cursor:"pointer", transition:"color 0.2s" }} onMouseEnter={e=>e.target.style.color=C.dustyRose} onMouseLeave={e=>e.target.style.color=C.muted}>{l}</div>
                ))}
              </div>
            ))}
          </div>

          {/* MOBILE FOOTER — Boutique, Shop, Socials, Support */}
          <div className="footer-mobile-cols" style={{ display:"none", grid:"auto/1fr 1fr", gap:24, marginBottom:32 }}>
            <div>
              <div style={{ color:C.noir, fontSize:9, letterSpacing:4, fontFamily:"'Raleway',sans-serif", marginBottom:16, textTransform:"uppercase", fontWeight:600 }}>Boutique</div>
              {["About Us","Live Sales","Restock Alerts","Contact"].map(l=>(
                <div key={l} style={{ color:C.muted, fontFamily:"sans-serif", fontSize:12, marginBottom:12 }}>{l}</div>
              ))}
            </div>
            <div>
              <div style={{ color:C.noir, fontSize:9, letterSpacing:4, fontFamily:"'Raleway',sans-serif", marginBottom:16, textTransform:"uppercase", fontWeight:600 }}>Shop</div>
              {["Best Sellers","Back In Stock","New Arrivals","Lightning Deals"].map(l=>(
                <div key={l} style={{ color:C.muted, fontFamily:"sans-serif", fontSize:12, marginBottom:12 }}>{l}</div>
              ))}
            </div>
            <div>
              <div style={{ color:C.noir, fontSize:9, letterSpacing:4, fontFamily:"'Raleway',sans-serif", marginBottom:16, textTransform:"uppercase", fontWeight:600 }}>Socials</div>
              <div style={{ display:"flex", gap:12 }}>
                <a href="mailto:PoshboutiqueNY@gmail.com" onClick={e=>{e.preventDefault();window.open("mailto:PoshboutiqueNY@gmail.com","_self");}} style={{ width:36, height:36, borderRadius:"50%", background:"rgba(196,132,154,0.08)", border:`1px solid rgba(196,132,154,0.2)`, display:"flex", alignItems:"center", justifyContent:"center", textDecoration:"none" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c4849a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/xoposhluxe" onClick={e=>{e.preventDefault();window.open("https://www.instagram.com/xoposhluxe","_blank");}} target="_blank" rel="noopener" style={{ width:36, height:36, borderRadius:"50%", background:"rgba(196,132,154,0.08)", border:`1px solid rgba(196,132,154,0.2)`, display:"flex", alignItems:"center", justifyContent:"center", textDecoration:"none" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c4849a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1" fill="#c4849a" stroke="none"/>
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <div style={{ color:C.noir, fontSize:9, letterSpacing:4, fontFamily:"'Raleway',sans-serif", marginBottom:16, textTransform:"uppercase", fontWeight:600 }}>Support</div>
              {["Shipping Info","All Sales Final","FAQ","Privacy Policy"].map(l=>(
                <div key={l} style={{ color: l==="All Sales Final" ? C.urgent : C.muted, fontFamily:"sans-serif", fontSize:12, marginBottom:12 }}>{l}</div>
              ))}
            </div>
          </div>
          <div style={{ borderTop:`1px solid rgba(196,132,154,0.08)`, paddingTop:28, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
            <div style={{ color:C.muted, fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:0.5, letterSpacing:1 }}>© 2026 Posh Boutique · All Rights Reserved · 🔒 Private Boutique</div>
            <div style={{ color:C.muted, fontFamily:"'Raleway',sans-serif", fontSize:10, letterSpacing:0.5 }}>Powered by Shopify · PoshboutiqueNY@gmail.com · @xoposhluxe</div>
          </div>
        </div>
      </footer>

      {showPopup && <SignupPopup onClose={()=>setShowPopup(false)}/>}
      {showCartRecovery && !cartDismissed && (
        <CartRecoveryBanner cartItems={cart} onRecover={()=>{setPage("cart");setShowCartRecovery(false);}} onDismiss={()=>{setCartDismissed(true);setShowCartRecovery(false);}}/>
      )}
    </div>
  );
}
